import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, deleteDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { addMarketerProduct, subscribeToMarketerProducts, subscribeToMarketerOrders } from '../../firebase/services'; 
import styles from './Marketer.module.css';
import MarketerOrderForm from '../../components/forms/MarketerOrderForm';
import { FaEdit, FaTimes, FaCheck, FaSpinner, FaSave } from 'react-icons/fa';

// Order Detail Modal Component for Marketers
const MarketerOrderDetailModal = ({ order, isOpen, onClose, onSave }) => {
  const [editedOrder, setEditedOrder] = useState({customerInfo: {}});
  const [saving, setSaving] = useState(false);
  
  // Helper to format prices
  const formatPrice = (price) => `$${price.toFixed(2)}`;
  
  useEffect(() => {
    if (order) {
      // Load directly from customerInfo if it exists, otherwise use flattened fields
      const customerInfo = order.customerInfo || {
        name: order.customerName || '',
        email: order.customerEmail || '',
        phone: order.customerPhone || '',
        address: order.customerAddress || '',
        notes: order.customerNotes || ''
      };
      
      setEditedOrder({ 
        ...order,
        customerInfo,
        totalAmount: order.totalAmount || 0,
        commission: order.commission || 0
      });
    }
  }, [order]);

  const handleInputChange = (field, value) => {
    // Always update both customerInfo and the flattened fields
    if (field.startsWith('customer')) {
      const customerField = field.replace('customer', '').toLowerCase();
      setEditedOrder({
        ...editedOrder,
        customerInfo: {
          ...editedOrder.customerInfo,
          [customerField]: value
        },
        // Also update the flattened field for consistency
        [field]: value
      });
    } else {
      setEditedOrder({
        ...editedOrder,
        [field]: value
      });
    }
  };

  const handleNumericChange = (field, value) => {
    const numericValue = parseFloat(value) || 0;
    setEditedOrder({
      ...editedOrder,
      [field]: numericValue
    });
  };

  const handleSave = async () => {
    if (!order) return;
    
    setSaving(true);
    const success = await onSave(editedOrder);
    setSaving(false);
    
    if (success) {
      onClose();
    }
  };
  
  if (!isOpen || !order) return null;
  
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Order #{order?.orderNumber || order?.id?.substring(0, 6)}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          <div className={styles.modalSection}>
            <h3>Order Status</h3>
            <span className={`${styles.statusBadge} ${styles[order?.status || 'pending']}`}>
              {order?.status || 'Pending'}
            </span>
            <p><strong>Date:</strong> {order?.formattedDate || 'N/A'}</p>
          </div>
          
          <div className={styles.modalSection}>
            <h3>Customer Information</h3>
            <div className={styles.formGroup}>
              <label>Name:</label>
              <input
                type="text"
                value={editedOrder?.customerInfo?.name || ''}
                onChange={(e) => handleInputChange('customerName', e.target.value)}
                placeholder="Customer Name"
              />
            </div>
            
            <div className={styles.formGroup}>
              <label>Email:</label>
              <input
                type="email"
                value={editedOrder?.customerInfo?.email || ''}
                onChange={(e) => handleInputChange('customerEmail', e.target.value)}
                placeholder="Customer Email"
              />
            </div>
            
            <div className={styles.formGroup}>
              <label>Phone:</label>
              <input
                type="tel"
                value={editedOrder?.customerInfo?.phone || ''}
                onChange={(e) => handleInputChange('customerPhone', e.target.value)}
                placeholder="Customer Phone"
              />
            </div>
            
            <div className={styles.formGroup}>
              <label>Address:</label>
              <textarea
                value={editedOrder?.customerInfo?.address || ''}
                onChange={(e) => handleInputChange('customerAddress', e.target.value)}
                placeholder="Customer Address"
                rows={3}
              />
            </div>
            
            <div className={styles.formGroup}>
              <label>Notes:</label>
              <textarea
                value={editedOrder?.customerInfo?.notes || ''}
                onChange={(e) => handleInputChange('customerNotes', e.target.value)}
                placeholder="Customer Notes"
                rows={3}
              />
            </div>
          </div>
          
          <div className={styles.modalSection}>
            <h3>Order Total</h3>
            <div className={styles.formGroup}>
              <label>Total Amount ($):</label>
              <input
                type="number"
                value={editedOrder?.totalAmount || 0}
                readOnly
                disabled
                className={styles.readOnlyField}
              />
            </div>
            
            <div className={styles.formGroup}>
              <label>Commission ($):</label>
              <input
                type="number"
                value={editedOrder?.commission || 0}
                readOnly
                disabled
                className={styles.readOnlyField}
              />
            </div>
          </div>
          
          {order.admin_note && (
            <div className={styles.modalSection}>
              <h3>Admin Notes</h3>
              <div className={styles.adminNotesContainer}>
                <p>{order.admin_note}</p>
              </div>
            </div>
          )}
          
          <div className={styles.modalSection}>
            <h3>Order Items</h3>
            {editedOrder?.items?.map((item, index) => (
              <div key={index} className={styles.orderItem}>
                <div className={styles.itemInfo}>
                  <span className={styles.itemQuantity}>{item?.quantity || 1}x</span>
                  <span className={styles.itemName}>{item?.name || 'Unknown Item'}</span>
                </div>
                <span className={styles.itemPrice}>${parseFloat(item?.price || 0).toFixed(2)}</span>
              </div>
            )) || <p>No items in this order</p>}
            
            <div className={styles.orderTotal}>
              <strong>Total:</strong> ${(editedOrder?.totalAmount || 0).toFixed(2)}
            </div>
          </div>
        </div>
        
        <div className={styles.modalFooter}>
          <button className={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button className={styles.saveButton} onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <FaSpinner className={styles.spinner} />
                Saving...
              </>
            ) : (
              <>
                <FaSave />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

function Marketer() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('products'); 
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [marketerName, setMarketerName] = useState('');
  const [availableProducts, setAvailableProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false); 
  const [orders, setOrders] = useState([]);
  const [lastVisibleOrder, setLastVisibleOrder] = useState(null);
  const [hasMoreOrders, setHasMoreOrders] = useState(true);
  const [isLoadingMoreOrders, setIsLoadingMoreOrders] = useState(false);
  const [ordersPerPage, setOrdersPerPage] = useState(10);
  const [userData, setUserData] = useState(null);
  
  // Order detail modal state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Form states
  const [newProduct, setNewProduct] = useState({
    productId: ''
  });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            if (data.role === 'marketer') {
              setIsAuthorized(true);
              setUserData(data);
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isAuthorized || !userData) {
      return;
    }

    const unsubscribe = subscribeToMarketerProducts(
      userData.marketerId,
      (products) => {
        setProducts(products);
      },
      (error) => console.error('Error fetching products:', error)
    );

    return () => {
      unsubscribe();
    };
  }, [isAuthorized, userData]);

  useEffect(() => {
    if (userData && userData.name) {
      setMarketerName(userData.name);
    }
  }, [userData]);

  useEffect(() => {
    loadCategories();
  }, []);

  const fetchAvailableProducts = async () => {
    try {
      let q;
      if (selectedCategory === 'all') {
        q = query(collection(db, 'products'));
      } else {
        q = query(
          collection(db, 'products'),
          where('category', '==', selectedCategory)
        );
      }
      const querySnapshot = await getDocs(q);
      const productsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAvailableProducts(productsData);
    } catch (err) {
      setError('Error loading available products');
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesRef = collection(db, 'categories');
      const snapshot = await getDocs(categoriesRef);
      const categoriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCategories(categoriesData);
    } catch (err) {
      setError('Error loading categories');
    }
  };

  const calculateCommission = (order) => {
    if (!order || !order.items) return 0;
    
    let totalCommission = 0;
    
    order.items.forEach(item => {
      if (!item) return;
      const commission = (item.commission || 0) * (item.quantity || 1);
      totalCommission += commission;
    });
    
    return totalCommission;
  };

  const formatPrice = (price) => `$${price.toFixed(2)}`;
  
  // Function to open the order details modal
  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };
  


  // Function to handle saving order changes
  const handleSaveOrderChanges = async (updatedOrder) => {
    try {
      const orderRef = doc(db, 'orders', updatedOrder.id);
      
      // First get the current document to check if it exists
      const orderDoc = await getDoc(orderRef);
      if (!orderDoc.exists()) {
        throw new Error('Order not found');
      }
      
      // Get the original order data to preserve any fields we're not changing
      const originalData = orderDoc.data();
      
      // Get the customer info from the updated order
      const { name, email, phone, address, notes } = updatedOrder.customerInfo;
      
      // Use only customerInfo object for better data structure
      
      const updates = {
        // Only update the customerInfo object - our single source of truth
        customerInfo: {
          ...(originalData.customerInfo || {}),
          name: name || '',
          email: email || '',
          phone: phone || '',
          address: address || '',
          notes: notes || ''
        },
        
        // Add updatedAt timestamp
        updatedAt: new Date()
      };
      
      // Update the order in Firestore
      await updateDoc(orderRef, updates);
      
      // Update local state - only customer information is updated
      setOrders(orders.map(order => 
        order.id === updatedOrder.id ? {
          ...order,
          customerName: updatedOrder.customerInfo.name,
          customerEmail: updatedOrder.customerInfo.email,
          customerPhone: updatedOrder.customerInfo.phone,
          customerAddress: updatedOrder.customerInfo.address,
          customerNotes: updatedOrder.customerInfo.notes
          // totalAmount and commission remain unchanged
        } : order
      ));
      
      return true;
    } catch (err) {
      setError('Failed to update order');
      return false;
    }
  };

  useEffect(() => {
    if (isAuthorized) {
      fetchAvailableProducts();
    }
  }, [selectedCategory, isAuthorized]);
  
  // Subscribe to orders for this marketer with pagination
  useEffect(() => {
    if (isAuthorized && auth.currentUser) {
      // Reset pagination when component mounts or authorization changes
      setLastVisibleOrder(null);
      setOrders([]);
      setHasMoreOrders(true);
      
      const unsubscribe = subscribeToMarketerOrders(
        auth.currentUser.uid, 
        (result) => {
          setOrders(result.orders);
          setLastVisibleOrder(result.lastVisible);
          setHasMoreOrders(result.hasMore);
        },
        ordersPerPage
      );
      
      return () => {
        unsubscribe();
      };
    }
  }, [isAuthorized, ordersPerPage]);

  // Function to load more orders with pagination
  const loadMoreOrders = async () => {
    if (!hasMoreOrders || isLoadingMoreOrders || !auth.currentUser) return;
    
    setIsLoadingMoreOrders(true);
    
    try {
      const unsubscribe = subscribeToMarketerOrders(
        auth.currentUser.uid,
        (result) => {
          setOrders(prevOrders => [...prevOrders, ...result.orders]);
          setLastVisibleOrder(result.lastVisible);
          setHasMoreOrders(result.hasMore);
          setIsLoadingMoreOrders(false);
          unsubscribe(); // Unsubscribe after receiving the data since we're just getting a page
        },
        ordersPerPage,
        lastVisibleOrder
      );
    } catch (error) {
      setIsLoadingMoreOrders(false);
    }
  };
  
  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    try {
      const selectedProduct = availableProducts.find(p => p.id === newProduct.productId);
      if (!selectedProduct) {
        setError('Please select a valid product');
        return;
      }

      const productData = {
        productId: selectedProduct.id,
        name: selectedProduct.name,
        description: selectedProduct.description,
        image: selectedProduct.imageUrl,
        category: selectedProduct.category,
        basePrice: selectedProduct.price,
        promotionalPrice: selectedProduct.isOffer ? selectedProduct.offerPrice : selectedProduct.price,
        isOffer: selectedProduct.isOffer,
        offerPrice: selectedProduct.isOffer ? selectedProduct.offerPrice : null,
        marketerId: auth.currentUser?.uid
      };

      const result = await addMarketerProduct(productData);
      
      if (result.success) {
        alert('Product added successfully!');
        setNewProduct({ productId: '' });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Error adding product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      await deleteDoc(doc(db, 'marketerProducts', productId));
    } catch (err) {
      setError('Error deleting product');
    }
  };

  if (loading) return <div className={styles.loading}>Loading...</div>;
  if (error || !isAuthorized) return <div className={styles.error}>{error || 'Unauthorized access'}</div>;

  return (
    <div className={styles.marketerContainer}>
      <h1 className={styles.marketerHeader}>{marketerName}'s Dashboard</h1>
      
      <div className={styles.marketerTabs}>
        <button 
          className={`${styles.tabButton} ${activeTab === 'products' ? styles.active : ''}`}
          onClick={() => setActiveTab('products')}
        >
          My Products
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'orders' ? styles.active : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          Create Orders
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'myOrders' ? styles.active : ''}`}
          onClick={() => setActiveTab('myOrders')}
        >
          My Orders
        </button>
      </div>

      {activeTab === 'products' && (
        <div className={styles.productsSection}>
          <form onSubmit={handleSubmitProduct} className={styles.productForm}>
            <h2>Add Product to Promote</h2>
            
            <div className={styles.formGroup}>
              <label>Select Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className={styles.selectInput}
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Select Product</label>
              <div className={styles.selectProduct}>
                <select
                  value={newProduct.productId}
                  onChange={(e) => setNewProduct({...newProduct, productId: e.target.value})}
                  className={styles.selectInput}
                  required
                >
                  <option value="">Select a product</option>
                  {availableProducts.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name} - {product.isOffer ? `Offer: $${product.offerPrice}` : `Base Price: $${product.price}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button 
              type="submit" 
              className={styles.submitButton}
              disabled={!newProduct.productId}
            >
              Add Product
            </button>
          </form>

          <div className={styles.promotedProducts}>
            <h2>My Promoted Products</h2>
            <div className={styles.productsGrid}>
              {(() => {
                // Apply category filter to displayed products
                const filteredProducts = selectedCategory === 'all'
                  ? products
                  : products.filter(product => product.category === selectedCategory);
                if (filteredProducts.length === 0) {
                  return (
                    <div className={styles.noProducts}>
                      <p>You haven't added any products yet</p>
                    </div>
                  );
                }
                return filteredProducts.map(product => (
                  <div key={product.id} className={styles.productCard}>
                    <img src={product.image} alt={product.name} className={styles.productImage} />
                    <div className={styles.productInfo}>
                      <h3>{product.name}</h3>
                      <p>{product.description}</p>
                      <div className={styles.productPrices}>
                        {product.isOffer ? (
                          <>
                            <span className={styles.originalPrice} style={{ textDecoration: 'line-through', color: '#888', marginRight: 8 }}>
                              ${product.basePrice}
                            </span>
                            <span className={styles.offerPrice} style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                              Offer: ${product.offerPrice}
                            </span>
                          </>
                        ) : (
                          <span className={styles.basePrice}>Price: ${product.basePrice}</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteProduct(product.id)}
                        className={styles.deleteButton}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'orders' && (
        <div className={styles.ordersSection}>
          <h2>Create New Order</h2>
          <MarketerOrderForm marketerProducts={products} />
        </div>
      )}

      {activeTab === 'myOrders' && (
        <div className={styles.ordersSection}>
          <h2>My Orders</h2>
          {orders.length === 0 ? (
            <div className={styles.noOrders}>
              <p>You don't have any orders yet</p>
            </div>
          ) : (
            <div className={styles.ordersContainer}>
              <div className={styles.orderFilters}>
                <h3>Order History</h3>
                <p>Total Orders: {orders.length}</p>
              </div>
              
              <div className={styles.ordersGrid}>
                {orders.map(order => (
                  <div key={order.id} className={styles.orderCard}>
                    <div className={styles.orderHeader}>
                      <span className={styles.orderNumber}>Order #{order.orderNumber ? order.orderNumber : 'N/A'}</span>
                      <span className={`${styles.orderStatus} ${styles[order.status]}`}>{order.status}</span>
                    </div>
                    
                    <div className={styles.orderInfo}>
                      <p><strong>Date:</strong> {order.formattedDate}</p>
                      <p><strong>Customer:</strong> {order.customerName}</p>
                      <p><strong>Total:</strong> ${order.totalAmount.toFixed(2)}</p>
                      <button className={styles.editButton} onClick={() => openOrderDetails(order)}>
                        <FaEdit /> Edit Order
                      </button>
                    </div>
                    
                    <div className={styles.orderItems}>
                      <h4>Items ({order.items.length})</h4>
                      <ul>
                        {order.items.map((item, index) => (
                          <li key={index} className={styles.orderItem}>
                            <div className={styles.itemInfo}>
                              <span className={styles.itemName}>{item.name}</span>
                              <span className={styles.itemQuantity}>x{item.quantity}</span>
                            </div>
                            <span className={styles.itemPrice}>
                              ${((item.finalPrice || item.price) * item.quantity).toFixed(2)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className={styles.customerDetails}>
                      <h4>Customer Details</h4>
                      <p><strong>Email:</strong> {order.customerEmail}</p>
                      <p><strong>Phone:</strong> {order.customerPhone}</p>
                      <p><strong>Address:</strong> {order.customerAddress}</p>
                      {order.customerNotes && (
                        <div className={styles.orderNotes}>
                          <p><strong>Notes:</strong> {order.customerNotes}</p>
                        </div>
                      )}
                      {order.admin_note && (
                        <div className={`${styles.orderNotes} ${styles.adminNote}`}>
                          <p><strong>Admin Note:</strong> {order.admin_note}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {hasMoreOrders && (
                <div className={styles.loadMoreContainer}>
                  <button 
                    className={styles.loadMoreButton}
                    onClick={loadMoreOrders}
                    disabled={isLoadingMoreOrders}
                  >
                    {isLoadingMoreOrders ? 'Loading...' : 'Load More Orders'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Order Detail Modal */}
      <MarketerOrderDetailModal 
        order={selectedOrder}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveOrderChanges}
      />
    </div>
  );
}

export default Marketer;